<script lang="ts" setup>
import {PhArrowRight, PhX} from "@phosphor-icons/vue";
import LogoGlobeOnly from "~/components/LogoGlobeOnly.vue";
import type {Page, SeoMeta} from "#magnolia-layer/types/magnolia-types";
import {joinURL} from "ufo";
import type {Ref} from "vue";
import type {NavigationRoot} from "#magnolia-layer/types/api";
import NavigationItem from "~/components/NavigationItem.vue";
import {useMagnoliaContext} from "#magnolia-layer/composables/use-magnolia-context";
import {useMagnoliaSitePath} from "#magnolia-layer/composables/use-magnolia-site-path";
import {EditorContextHelper} from "@magnolia/vue-editor";

// Hoist all composable calls before any await
const router = useRouter();
const route = useRoute();
const runtimeConfig = useRuntimeConfig();
const magnoliaContext = useMagnoliaContext();
const magnoliaLanguages = useMagnoliaLanguages();
const magnoliaSitePath = useMagnoliaSitePath();

const spineOpen = useState("spineOpen", () => true);
const open = useState("mobileOpen", () => false);
const spineTitleState = useState<string>("spineTitle");

const toggleNav = (isWrapper = false) => {
  const isAboveSmall = window.matchMedia("(min-width: 640px").matches;
  if (isWrapper && !isAboveSmall) {
    return;
  }
  if (isAboveSmall) {
    spineOpen.value = !spineOpen.value;
  } else {
    open.value = !open.value;
  }
};

router.afterEach((to, from) => {
  open.value = false;
});

const spineTitle = computed(() => {
  return spineTitleState.value ?? "";
});

const spineTitleDisplay = ref();
const spineTitleDisplayMobile = ref();
const defaultSpineTile = "Menu";

const siteRootNode = magnoliaContext.nodePath?.split("/").filter((v) => v)[0];
const apiPages = runtimeConfig.public.mgnl.apiPages;

// Reactive magnolia context derived from route changes without calling composables
const REPLACE = `(^${magnoliaSitePath})|(^/(${magnoliaLanguages.join("|")})+?${magnoliaSitePath})`;
const reactiveNodePath = computed(() => {
  const context = EditorContextHelper.getMagnoliaContext(
    route.fullPath,
    REPLACE,
    magnoliaLanguages,
  );
  return context.nodePath?.replace(REPLACE, magnoliaSitePath);
});

const primaryNav: Ref<NavigationRoot | null> = ref(null);

const primaryNavigationItems = computed(() => {
  return resolveNavigation(primaryNav.value) ?? [];
});

watch(
  spineTitle,
  (n, o) => {
    if (!n) {
      spineTitleDisplay.value = defaultSpineTile;
      spineTitleDisplayMobile.value = "";
      return;
    }
    const nodePath = reactiveNodePath.value;
    if (nodePath?.split("/")) {
      const level = nodePath.split("/").length - 1;
      if (level === 1) {
        spineTitleDisplay.value = defaultSpineTile;
        spineTitleDisplayMobile.value = "";
        return;
      }
    }
    spineTitleDisplay.value = n;
    spineTitleDisplayMobile.value = n;
  },
  {
    immediate: true,
  },
);

const {data: root} = await useMagnoliaFetch<
  Page & {
    title: string;
  } & SeoMeta
>(joinURL(apiPages, siteRootNode ?? ""));

const mainNavigationId = root.value?.["primaryNavigation"];

/**
 * The downstream fetch call uses the currentLanguage within the Magnolia Context.
 * Watch route.fullPath to re-fetch navigation when language/route changes.
 */
watch(
  () => route.fullPath,
  async () => {
    const n = await resolveNavigationReference(mainNavigationId);
    primaryNav.value = n.value;
  },
  {
    immediate: true,
  },
);
</script>

<template>
  <div
    id="nav-wrapper"
    :class="{open: spineOpen, mobileOpen: open}"
    class="p-2 sm:p-4 text-white border-b-[1px] border-neutral-600 select-none"
    @click.prevent="toggleNav(true)">
    <button
      :aria-expanded="spineOpen ? 'true' : 'false'"
      :aria-label="
        spineOpen
          ? $t('navigation.closeNavigation')
          : $t('navigation.openNavigation')
      "
      aria-controls="main-navigation"
      class="spine-toggle order-[999] ml-auto sm:mx-auto z-20 hidden sm:block"
      @click.stop="toggleNav()">
      <PhArrowRight :size="40"></PhArrowRight>
    </button>

    <button
      :aria-expanded="open ? 'true' : 'false'"
      :aria-label="
        open
          ? $t('navigation.closeNavigation')
          : $t('navigation.openNavigation')
      "
      aria-controls="main-navigation"
      class="nav-toggle order-[999] ml-auto sm:mx-auto z-20 inline-flex items-center gap-2 sm:hidden"
      @click.stop="toggleNav()">
      <transition mode="out-in" name="fade">
        <span :key="open ? 'Close' : 'Menu'">{{
          open ? "Close" : "Menu"
        }}</span>
      </transition>
      <transition mode="out-in" name="fade">
        <div :key="open ? 'Close' : 'Menu'">
          <PhArrowRight v-if="!open" :size="24"></PhArrowRight>
          <PhX v-else :size="24"></PhX>
        </div>
      </transition>
    </button>
    <!-- TODO: Check if needed :aria-label="$t('navigation.goHome')" -->
    <NuxtLinkLocale class="logo" to="/">
      <Logo :show-word-mark="spineOpen" class="hidden sm:block"></Logo>
      <LogoGlobeOnly class="block sm:hidden"></LogoGlobeOnly>
    </NuxtLinkLocale>

    <transition mode="out-in" name="fade-y">
      <div
        :key="spineTitleDisplay"
        class="spine text-3xl pointer-events-none invisible sm:visible">
        {{ spineTitleDisplay }}
      </div>
    </transition>
    <transition mode="out-in" name="fade-y">
      <div
        :key="spineTitleDisplayMobile"
        class="spine text-lg pointer-events-none visible sm:invisible">
        {{ spineTitleDisplayMobile }}
      </div>
    </transition>

    <div class="menu-wrapper bg-black flex flex-col items-center">
      <NuxtImg class="sm:hidden" densities="1x" src="logo.svg"></NuxtImg>

      <nav
        id="main-navigation"
        class="text-2xl mt-8 gap-4 pl-10 flex flex-col w-fit sm:w-full"
        role="navigation"
        :aria-label="$t('navigation.main')">
        <template v-for="item in primaryNavigationItems">
          <NavigationItem :item></NavigationItem>
        </template>
        <LanguageSelector />
      </nav>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.logo > * {
  shape-rendering: geometricPrecision;
  height: auto;
}

.fade-enter-active,
.fade-leave-active {
  transition: all var(--transition-duration) var(--transition-easing);
}

.fade-leave-to {
  opacity: 0;
}

.fade-enter-from {
  opacity: 0;
}

.fade-y-enter-active,
.fade-y-leave-active {
  transition: all var(--transition-duration) var(--transition-easing);
}

.fade-y-leave-to {
  opacity: 0;
  transform: translateY(-50px);
}

.fade-y-enter-from {
  opacity: 0;
  transform: translateY(50px);
}

.nav-toggle,
.spine-toggle {
  appearance: none;
  border: none;
  background: none;
  color: #fff;
  cursor: pointer;
  transition: all var(--transition-duration) var(--transition-easing);

  justify-content: center;
  align-content: center;
  align-items: center;

  svg {
    transition: transform var(--transition-duration) var(--transition-easing);
  }

  &:hover {
    color: var(--color-sunrise-100);
  }
}

#nav-wrapper.open .spine-toggle svg {
  transform: rotate(180deg);
}

#main-navigation {
  a {
    color: #fff;
    text-decoration: none;

    transition: all 0.3s ease;

    &:hover {
      color: var(--color-sunrise-100);
    }
  }
}

.menu-wrapper {
  transition: all var(--transition-duration) var(--transition-easing);
  opacity: 0;

  padding-top: 72px;
  position: fixed;
  inset: 0;
  height: 100dvh;
  width: 100vw;

  background-color: #000;
  @media (max-width: 639px) {
    transform: translateX(-100%);
  }
  @screen sm {
    background-color: transparent;
    pointer-events: none;
    position: static;
    padding-top: 0;
    height: auto;
    width: auto;
  }
}

.spine {
  color: #fff;
  transition: all var(--transition-duration) var(--transition-easing);
  user-select: none;
  display: inline-flex;
  grid-column: 2;
  grid-row: 1;

  @media (min-width: 640px) {
    text-align: center;
    align-self: center;
    rotate: -90deg;
    position: absolute;
    white-space: nowrap;
    //inset: 0;
    aspect-ratio: 1;
    justify-content: center;
    align-items: center;
  }
}

#nav-wrapper {
  width: 100%;
  height: 72px;

  display: grid;
  align-items: center;

  grid-template-columns: auto 1fr auto;

  transition: all var(--transition-duration) var(--transition-easing);
  overflow: hidden;

  gap: 16px;
  @media (min-width: 640px) {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr auto;
    grid-column: 1;
    grid-row: 1 / -1;
    height: 100vh;
    width: 120px;
    border-right: 1px solid var(--color-neutral-600);
    border-bottom: none;

    justify-content: center;
    align-items: start;
    justify-items: center;

    padding: 32px;

    cursor: pointer;
    &:hover {
      background-color: var(--color-neutral-900);
    }
  }

  @media (min-width: 640px) {
    &.open {
      width: 306px;

      .menu-wrapper {
        opacity: 1;
        pointer-events: all;
        transform: translateX(0%);
      }

      .spine {
        opacity: 0;
      }
    }
  }

  @media (max-width: 639px) {
    &.mobileOpen {
      .menu-wrapper {
        opacity: 1;
        transform: translateX(0%);
        pointer-events: all;
      }
    }
  }
}

.border-toggle {
  background-color: #fff;
  appearance: none;
  border: none;
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 100%;
  opacity: 0;
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    opacity: 0.15;
  }
}
</style>
